import { useCreateSummary } from '@repo/api-codegen';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ISecodaEditor } from '../../types';
import { ToastType } from '../../types';

interface UseAISummaryProps {
	editor: ISecodaEditor;
	initialSummary?: string;
	onShowToast?: (message: string, type: ToastType) => void;
}

export function useAISummary({
	editor,
	initialSummary,
	onShowToast,
}: UseAISummaryProps) {
	const ignoreRef = useRef(false);

	const [summary, setSummary] = useState(initialSummary ?? '');
	const { mutateAsync, isPending: isLoading } = useCreateSummary({});

	const generate = useCallback(async () => {
		try {
			if (!editor.view) {
				onShowToast?.('Editor is not initialized', ToastType.Error);
				return;
			}

			const document = editor.serializer.serialize(editor.view.state.doc);

			if (document?.trim()?.length < 50) {
				onShowToast?.('Document is too short', ToastType.Error);
				return;
			}

			const response = await mutateAsync({
				body: {
					content: document,
				},
			});

			if (ignoreRef.current) {
				return;
			}

			setSummary(response.content ?? '');
		} catch {
			onShowToast?.('Error creating summary', ToastType.Error);
		}
	}, [editor, mutateAsync, onShowToast]);

	useEffect(() => {
		if (!initialSummary) {
			generate();
		}

		return () => {
			ignoreRef.current = true;
		};
	}, [generate, initialSummary]);

	return {
		summary,
		generate,
		isLoading,
	};
}
